import { useEffect, useState } from 'react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { getMember, schedulePickup, cancelPickup, sendSms } from '@/lib/pickups-api';
import type { MemberLookupResult } from '@/lib/pickups-types';
import {
  verdictPill,
  statusPill,
  ineligibleReason,
  smsBlockedLabel,
} from '@/lib/pickups-display';
import { fmtCdxDate, fmtCdxDateTime } from '@/lib/dates';
import { cn } from '@/lib/cn';

/**
 * Member Lookup - the default landing page. Search a member by id (or name substring), render the
 * member card + eligibility verdict + any existing pickup, and (ADMIN only) schedule / cancel /
 * SMS. The schedule action is disabled unless the verdict bucket is 'eligible'.
 */

interface MemberLookupProps {
  /** Admin gate from GET /api/pickups/me - hides the mutating actions for operators. */
  isAdmin: boolean;
  /** Deep-link target id (set when a row in Eligible Members is clicked). */
  initialId?: string | undefined;
  /** Cleared after the deep-link id has been consumed so re-navigation re-triggers. */
  onConsumeInitialId?: (() => void) | undefined;
}

function fullAddress(parts: { address: string; city: string; state: string; zip: string }): string {
  const line2 = [parts.city, parts.state].filter(Boolean).join(', ');
  return [parts.address, [line2, parts.zip].filter(Boolean).join(' ')].filter(Boolean).join(' · ');
}

export function MemberLookup({ isAdmin, initialId, onConsumeInitialId }: MemberLookupProps) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MemberLookupResult | null>(null);

  // Schedule-form state.
  const [pickupDate, setPickupDate] = useState('');
  const [notifySms, setNotifySms] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  async function runLookup(id: string) {
    const trimmed = id.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    setActionMsg(null);
    setResult(null);
    // Reset the schedule-form state so member A's date / "also text" toggle does not bleed into
    // member B's form (this path also covers the deep-link lookup).
    setPickupDate('');
    setNotifySms(false);
    try {
      const res = await getMember(trimmed);
      setResult(res);
    } catch (e) {
      const status = (e as { status?: number }).status;
      setError(
        status === 404
          ? `No member found for "${trimmed}" in the 26fobt roster.`
          : `Lookup failed${status ? ` (HTTP ${status})` : ''}. Try again.`,
      );
    } finally {
      setLoading(false);
    }
  }

  // Consume a deep-link id once.
  useEffect(() => {
    if (initialId && initialId.trim()) {
      setQuery(initialId);
      void runLookup(initialId);
      onConsumeInitialId?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialId]);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    void runLookup(query);
  }

  async function onSchedule() {
    if (!result) return;
    setActionBusy(true);
    setActionMsg(null);
    try {
      const res = await schedulePickup({
        memberId: result.member.memberId,
        pickupDate: pickupDate.trim() || undefined,
        notifySms,
      });
      if (res.ok) {
        const rec = res.data as { confirmationNumber?: string | null; status?: string };
        if (rec.status === 'scheduled') {
          setActionMsg({
            kind: 'ok',
            text: `Pickup scheduled. Confirmation ${rec.confirmationNumber ?? '(pending)'}.`,
          });
        } else {
          const err = (res.data as { error?: string }).error;
          setActionMsg({ kind: 'err', text: `USPS could not schedule the pickup. ${err ?? ''}`.trim() });
        }
      } else {
        const err = (res.data as { error?: string }).error ?? `HTTP ${res.status}`;
        setActionMsg({ kind: 'err', text: err });
      }
      // Refresh the member to pick up the new pickup record.
      await runLookup(result.member.memberId);
    } catch (e) {
      setActionMsg({ kind: 'err', text: `Schedule failed: ${String((e as Error).message)}` });
    } finally {
      setActionBusy(false);
    }
  }

  async function onCancel() {
    if (!result || !result.pickup || !result.pickup.confirmationNumber) return;
    setActionBusy(true);
    setActionMsg(null);
    try {
      const res = await cancelPickup({
        memberId: result.member.memberId,
        confirmationNumber: result.pickup.confirmationNumber,
      });
      if (res.ok) {
        setActionMsg({ kind: 'ok', text: 'Pickup canceled.' });
      } else {
        const err = (res.data as { error?: string }).error ?? `HTTP ${res.status}`;
        setActionMsg({ kind: 'err', text: err });
      }
      await runLookup(result.member.memberId);
    } catch (e) {
      setActionMsg({ kind: 'err', text: `Cancel failed: ${String((e as Error).message)}` });
    } finally {
      setActionBusy(false);
    }
  }

  async function onSms() {
    if (!result) return;
    setActionBusy(true);
    setActionMsg(null);
    try {
      const res = await sendSms({ memberId: result.member.memberId, kind: 'pickup-reminder' });
      const data = res.data as { ok?: boolean; blocked?: 'do_not_solicit' | 'no_phone' };
      if (res.ok && data.ok) {
        setActionMsg({ kind: 'ok', text: 'SMS intent recorded (stub - no message was actually sent).' });
      } else if (data.blocked) {
        setActionMsg({ kind: 'err', text: smsBlockedLabel(data.blocked) });
      } else {
        const err = (res.data as { error?: string }).error ?? `HTTP ${res.status}`;
        setActionMsg({ kind: 'err', text: err });
      }
    } catch (e) {
      setActionMsg({ kind: 'err', text: `SMS failed: ${String((e as Error).message)}` });
    } finally {
      setActionBusy(false);
    }
  }

  const member = result?.member;
  const verdict = result?.eligibility;
  const pickup = result?.pickup;
  const eligible = verdict?.bucket === 'eligible';
  const vp = verdict ? verdictPill(verdict.bucket) : null;
  const hasActivePickup = pickup?.status === 'scheduled';

  return (
    <div className="page-pad">
      <header>
        <div className="section-eyebrow">CDX Pickups · Member</div>
        <h1 className="section-title">
          Member <em>Lookup</em>
        </h1>
        <div className="section-rule" />
        <p className="section-lede">
          Look up a member by id (or name substring) to see carrier-pickup eligibility, the
          USPS-standardized address, and any pickup already on file.
        </p>
      </header>

      <section>
        <form className="card" onSubmit={onSubmit}>
          <div className="field-row" style={{ marginBottom: 0, gridTemplateColumns: '1fr auto' }}>
            <div>
              <label className="field-label" htmlFor="ml-q">Member id or name</label>
              <input
                id="ml-q"
                className="text-input"
                placeholder="e.g. 100245 or Garcia"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button type="submit" className="btn btn-primary" disabled={loading || !query.trim()}>
                {loading ? 'Searching…' : 'Look up'}
              </button>
            </div>
          </div>
        </form>
      </section>

      {loading && (
        <section>
          <div className="card">
            <LoadingSpinner label="Loading member…" />
          </div>
        </section>
      )}

      {error && !loading && (
        <section>
          <div className="note danger">
            <b>Not found.</b> {error}
          </div>
        </section>
      )}

      {member && verdict && !loading && (
        <>
          {/* ── Member card ─────────────────────────────────────────── */}
          <section>
            <div className="block-head">
              Member
              <span className="aux">{member.memberId}</span>
            </div>
            <div className="card">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 16,
                  marginBottom: 14,
                }}
              >
                <div>
                  <div className="numeric-heading" style={{ fontFamily: 'var(--font-serif)' }}>
                    {member.fullName}
                  </div>
                  <div className="mono" style={{ fontSize: 11, color: 'var(--ink-400)', marginTop: 2 }}>
                    Member {member.memberId} · Insurance {member.insuranceId || '--'}
                  </div>
                </div>
                {vp && <span className={cn('pill', vp.cls)}>{vp.label}</span>}
              </div>

              <div className="leader-row">
                <span className="k">Language</span>
                <span className="leader" />
                <span className="v">{member.language || '--'}</span>
              </div>
              <div className="leader-row">
                <span className="k">Kit stage</span>
                <span className="leader" />
                <span className="v mono">{member.kitStage || '--'}</span>
              </div>
              <div className="leader-row">
                <span className="k">Phone</span>
                <span className="leader" />
                <span className="v mono">{member.phone1 || member.phone2 || '--'}</span>
              </div>
              <div className="leader-row">
                <span className="k">Roster address</span>
                <span className="leader" />
                <span className="v">{fullAddress(member)}</span>
              </div>
              {eligible && verdict.stdAddress && (
                <div className="leader-row">
                  <span className="k">USPS standardized</span>
                  <span className="leader" />
                  <span className="v">
                    {verdict.stdAddress}
                    {verdict.stdZip4 ? ` · ${verdict.stdZip4}` : ''}
                  </span>
                </div>
              )}
              {member.doNotSolicit && (
                <div className="note warn" style={{ marginTop: 12 }}>
                  <b>Do not solicit.</b> This member is flagged do-not-solicit; SMS is blocked.
                </div>
              )}
            </div>
          </section>

          {/* ── Existing pickup ─────────────────────────────────────── */}
          {pickup && (
            <section>
              <div className="block-head">
                Existing Pickup
                <span className="aux">on file</span>
              </div>
              <div className="card">
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}
                >
                  <span className={cn('pill', statusPill(pickup.status).cls)}>
                    {statusPill(pickup.status).label}
                  </span>
                  {pickup.confirmationNumber && (
                    <span className="mono" style={{ fontSize: 12 }}>
                      Conf {pickup.confirmationNumber}
                    </span>
                  )}
                </div>
                <div className="leader-row">
                  <span className="k">Pickup date</span>
                  <span className="leader" />
                  <span className="v mono">{fmtCdxDate(pickup.pickupDate) || '--'}</span>
                </div>
                <div className="leader-row">
                  <span className="k">Scheduled by</span>
                  <span className="leader" />
                  <span className="v">{pickup.scheduledByEmail || '--'}</span>
                </div>
                <div className="leader-row">
                  <span className="k">Scheduled at</span>
                  <span className="leader" />
                  <span className="v mono">{fmtCdxDateTime(pickup.scheduledAt) || '--'}</span>
                </div>
                {pickup.error && (
                  <div className="note danger" style={{ marginTop: 12 }}>
                    <b>USPS error.</b> {pickup.error}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* ── Admin actions ───────────────────────────────────────── */}
          {isAdmin && (
            <section>
              <div className="block-head">
                Actions
                <span className="aux">admin</span>
              </div>
              <div className="card">
                {actionMsg && (
                  <div className={cn('note', actionMsg.kind === 'err' ? 'danger' : '')} style={{ marginBottom: 14 }}>
                    <b>{actionMsg.kind === 'err' ? 'Failed.' : 'Done.'}</b> {actionMsg.text}
                  </div>
                )}

                {/* Schedule */}
                <div style={{ marginBottom: 18 }}>
                  <div className="micro" style={{ marginBottom: 8 }}>Send for pickup</div>
                  <div className="field-row" style={{ marginBottom: 10 }}>
                    <div>
                      <label className="field-label" htmlFor="ml-date">Pickup date (optional)</label>
                      <input
                        id="ml-date"
                        type="date"
                        className="text-input"
                        value={pickupDate}
                        onChange={(e) => setPickupDate(e.target.value)}
                        disabled={!eligible || actionBusy}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <label
                        className="micro"
                        style={{ display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 8 }}
                      >
                        <input
                          type="checkbox"
                          checked={notifySms}
                          onChange={(e) => setNotifySms(e.target.checked)}
                          disabled={!eligible || actionBusy || member.doNotSolicit}
                        />
                        Also text the member
                      </label>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-teal"
                    onClick={onSchedule}
                    disabled={!eligible || actionBusy || hasActivePickup}
                    title={!eligible ? ineligibleReason(verdict.bucket) : hasActivePickup ? 'A pickup is already scheduled for this member.' : undefined}
                  >
                    {actionBusy ? 'Working…' : 'Send for pickup'}
                  </button>
                  {!eligible && (
                    <div className="micro" style={{ marginTop: 8, color: 'var(--crimson)' }}>
                      {ineligibleReason(verdict.bucket)}
                    </div>
                  )}
                  {eligible && hasActivePickup && (
                    <div className="micro" style={{ marginTop: 8, color: 'var(--ink-400)' }}>
                      A pickup is already scheduled for this member. Cancel it before scheduling another.
                    </div>
                  )}
                </div>

                {/* SMS */}
                <div style={{ marginBottom: hasActivePickup ? 18 : 0 }}>
                  <div className="micro" style={{ marginBottom: 8 }}>Send SMS</div>
                  <button
                    type="button"
                    className="btn"
                    onClick={onSms}
                    disabled={actionBusy || member.doNotSolicit || !(member.phone1 || member.phone2)}
                  >
                    {actionBusy ? 'Working…' : 'Send SMS'}
                  </button>
                  <div className="micro" style={{ marginTop: 8, color: 'var(--ink-400)' }}>
                    Stub - send not yet wired. Records the intent only.
                  </div>
                </div>

                {/* Cancel */}
                {hasActivePickup && (
                  <div>
                    <div className="micro" style={{ marginBottom: 8 }}>Cancel pickup</div>
                    <button type="button" className="btn btn-danger" onClick={onCancel} disabled={actionBusy}>
                      {actionBusy ? 'Working…' : 'Cancel pickup'}
                    </button>
                  </div>
                )}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
